import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NumericInput } from '../../components/NumericInput';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Save, Loader2, Upload, Trash2, FileText, Eye, X, Search,
  AlertTriangle, Shield, Droplets, Layers, Wrench, Users, ClipboardList, MapPin, CheckCircle2,
  ImageIcon, ClipboardPaste, Check, Plus, UserCheck
} from 'lucide-react';
import { useDownload, formatBytes } from '../../context/DownloadContext';
import { SubmittalPreviewModal } from '../../components/SubmittalPreviewModal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import ClipboardPasteModal from '../../components/ClipboardPasteModal';
import { useSubmittalsManager, commitSubmittals } from '../shared/useSubmittalsManager';
import { WizardSubmittalsStep, WizardSubmittalsReview } from '../shared/WizardSubmittalsStep';
import { WizardStepper, FormField, FormSelect, ReviewSection, ReviewProductHeader } from '../shared/WizardComponents';
import { ZoomControls } from '../shared/ElevationViewer';
import { COUNTRIES, getCountryName } from '../../utils/countries';

const TOTAL_STEPS = 11;

const JOINT_TYPES = ['Sealant joint', 'Expansion joint system', 'Control joint', 'Fire-rated joint'];
const LOCATIONS = ['Façade', 'Slab edge', 'Floor-to-floor', 'Window perimeter'];
const MOVEMENT_TYPES = ['Expansion / contraction', 'Shear', 'Multi-directional'];
const ADJACENT_MATERIALS = ['Concrete', 'Metal', 'Glass', 'EIFS', 'CMU'];
const INTERFACES_WITH = ['Air Barrier', 'WRB', 'Flashing', 'Windows / Curtain Wall'];
const DOCUMENT_TYPES = ['Product data', 'Test reports', 'Installation guide', 'Warranty letter'];
const MARKER_TYPES = ['Expansion joint', 'Sealant joint'];
const UV_RATINGS = ['Low', 'Medium', 'High'];

const sanitizeFraction = (v) => v.replace(/[^0-9./]/g, '');
const sanitizeNumeric = (v) => v.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

const STEP_LABELS = [
  { id: 1, title: 'Product Identification' },
  { id: 2, title: 'Movement Capability' },
  { id: 3, title: 'Fire & Life Safety' },
  { id: 4, title: 'Environmental Exposure' },
  { id: 5, title: 'Substrate Compatibility' },
  { id: 6, title: 'Installation Parameters' },
  { id: 7, title: 'Interface Responsibility' },
  { id: 8, title: 'Warranty & Maintenance' },
  { id: 9, title: 'Submittals' },
  { id: 10, title: 'Visual Coverage' },
  { id: 11, title: 'Review & Summary' },
];

const INITIAL_FORM = {
  productNameModel: '', manufacturerId: '', countryCode: '', imageFile: null, imagePreview: '',
  jointType: '', location: [],
  movementCapability: { movementRating: '', jointWidthMin: '', jointWidthMax: '', movementType: '' },
  fireLifeSafety: { fireRating: '', testedAssembly: '', fireResistiveContinuity: '' },
  environmentalExposure: { uvExposureRating: '', waterResistance: '', exteriorInteriorUse: '', chemicalResistance: '' },
  substrateCompatibility: { adjacentMaterials: [], primerRequired: '', manufacturerCompatConfirmation: '' },
  installationParameters: { installationMethod: '', jointPreparation: '', temperatureRange: '', backerRodRequired: '' },
  interfaceResponsibility: { interfacesWith: '', responsibility: '', otherTrade: '' },
  warrantyMaintenance: { manufacturerWarranty: '', installerWarranty: '', resealingInterval: '', maintenanceResponsibility: '' },
  visualJointLocation: { elevationFileName: '', elevationUrl: '', elevationPublicId: '', markers: [], markerType: '' },
  submittals: [], qrToken: '',
};

const STORAGE_PREFIX = 'specard_jp_wizard_v1_';
const saveWizardState = (companyId, key, state) => { try { sessionStorage.setItem(`${STORAGE_PREFIX}${companyId}_${key}`, JSON.stringify(state)); } catch {} };
const loadWizardState = (companyId, key) => { try { const d = sessionStorage.getItem(`${STORAGE_PREFIX}${companyId}_${key}`); return d ? JSON.parse(d) : null; } catch { return null; } };
const clearWizardState = (companyId, key) => { try { sessionStorage.removeItem(`${STORAGE_PREFIX}${companyId}_${key}`); } catch {} };
const mergeFormDefaults = (f) => {
  const d = JSON.parse(JSON.stringify(INITIAL_FORM));
  const merged = { ...d, ...f };
  merged.movementCapability = { ...d.movementCapability, ...f.movementCapability };
  merged.fireLifeSafety = { ...d.fireLifeSafety, ...f.fireLifeSafety };
  merged.environmentalExposure = { ...d.environmentalExposure, ...f.environmentalExposure };
  merged.substrateCompatibility = { ...d.substrateCompatibility, ...f.substrateCompatibility };
  merged.installationParameters = { ...d.installationParameters, ...f.installationParameters };
  merged.interfaceResponsibility = { ...d.interfaceResponsibility, ...f.interfaceResponsibility };
  merged.warrantyMaintenance = { ...d.warrantyMaintenance, ...f.warrantyMaintenance };
  const vjl = f.visualJointLocation || {};
  merged.visualJointLocation = {
    ...d.visualJointLocation,
    ...vjl,
    elevationFileName: vjl.elevationFileName || '',
    elevationUrl: vjl.elevationUrl || '',
    elevationPublicId: vjl.elevationPublicId || '',
    markers: vjl.markers || [],
    markerType: vjl.markerType || '',
  };
  return merged;
};

const AddJointProtectionWizard = ({ open, onOpenChange, projectId, companyId, csiItemId, editProduct, onRefresh }) => {
  const isEdit = !!editProduct;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [mfrSearch, setMfrSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [previewSubmittal, setPreviewSubmittal] = useState(null);
  const [manufacturers, setManufacturers] = useState([]);
  const [mfrDropdownOpen, setMfrDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteUploading, setPasteUploading] = useState(false);
  const [creatingMfr, setCreatingMfr] = useState(false);
  const [elevationUploading, setElevationUploading] = useState(false);
  const [elevationZoom, setElevationZoom] = useState(1);
  const fileInputRef = useRef(null);
  const initializedRef = useRef(false);
  const initialFormRef = useRef(INITIAL_FORM);
  const mfrRef = useRef(null);
  const countryRef = useRef(null);
  const submittals = useSubmittalsManager();

  // Init: Edit or Restore
  useEffect(() => {
    if (!open || initializedRef.current) return;
    initializedRef.current = true;
    if (editProduct) {
      const editForm = mergeFormDefaults({ ...editProduct, imageFile: null, imagePreview: editProduct.imageUrl || editProduct.imageCloudinary?.urls?.medium || '' });
      setForm(editForm);
      initialFormRef.current = mergeFormDefaults({ ...editForm, qrToken: undefined });
      setDraftId(editProduct.id);
      submittals.init(editProduct.submittals || []);
      setStep(1);
    } else {
      clearWizardState(companyId, 'new');
      setForm({ ...INITIAL_FORM }); setStep(1); setDraftId(null); initialFormRef.current = { ...INITIAL_FORM };
      submittals.init([]);
    }
  }, [open, editProduct, companyId, submittals]);

  useEffect(() => { if (!open) initializedRef.current = false; }, [open]);

  // Fetch manufacturers (shared Div 7 collection)
  useEffect(() => {
    axios.get(`${API}/thermal-insulation-manufacturers`).then(r => setManufacturers(r.data || [])).catch(() => {});
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const h = (e) => { if (mfrRef.current && !mfrRef.current.contains(e.target)) setMfrDropdownOpen(false); if (countryRef.current && !countryRef.current.contains(e.target)) setCountryDropdownOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  const filteredMfrs = manufacturers.filter(m => m.name?.toLowerCase().includes(mfrSearch.toLowerCase()));
  const selectedMfr = manufacturers.find(m => m.id === form.manufacturerId);
  const showAddNew = mfrSearch.trim() && !filteredMfrs.some(m => m.name?.toLowerCase() === mfrSearch.trim().toLowerCase());

  const handleAddMfr = async () => {
    if (creatingMfr) return;
    setCreatingMfr(true);
    try {
      const res = await axios.post(`${API}/thermal-insulation-manufacturers`, { name: mfrSearch.trim(), companyId });
      const newMfr = res.data;
      setManufacturers(prev => [...prev, newMfr].sort((a, b) => a.name.localeCompare(b.name)));
      setField('manufacturerId', newMfr.id);
      setMfrDropdownOpen(false);
      setMfrSearch('');
    } catch { toast.error('Failed to add manufacturer'); }
    finally { setCreatingMfr(false); }
  };
  const filteredCountries = COUNTRIES.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.toLowerCase().includes(countrySearch.toLowerCase()));
  const selectedCountry = COUNTRIES.find(c => c.code === form.countryCode);

  const isReviewStep = step === TOTAL_STEPS;

  const isDirty = (() => {
    if (submittals.isDirty) return true;
    const i = initialFormRef.current;
    return form.productNameModel !== i.productNameModel || form.manufacturerId !== i.manufacturerId ||
      form.countryCode !== i.countryCode || !!form.imageFile ||
      form.jointType !== i.jointType ||
      JSON.stringify(form.location) !== JSON.stringify(i.location) ||
      JSON.stringify(form.movementCapability) !== JSON.stringify(i.movementCapability) ||
      JSON.stringify(form.fireLifeSafety) !== JSON.stringify(i.fireLifeSafety) ||
      JSON.stringify(form.environmentalExposure) !== JSON.stringify(i.environmentalExposure) ||
      JSON.stringify(form.substrateCompatibility) !== JSON.stringify(i.substrateCompatibility) ||
      JSON.stringify(form.installationParameters) !== JSON.stringify(i.installationParameters) ||
      JSON.stringify(form.interfaceResponsibility) !== JSON.stringify(i.interfaceResponsibility) ||
      JSON.stringify(form.warrantyMaintenance) !== JSON.stringify(i.warrantyMaintenance) ||
      form.visualJointLocation.elevationFileName !== i.visualJointLocation.elevationFileName ||
      JSON.stringify(form.visualJointLocation.markers) !== JSON.stringify(i.visualJointLocation.markers);
  })();

  const setField = (field, val) => setForm(prev => ({ ...prev, [field]: val }));
  const setNested = (section, field, val) => setForm(prev => ({ ...prev, [section]: { ...prev[section], [field]: val } }));
  const toggleArrayItem = (field, val) => setForm(prev => ({ ...prev, [field]: prev[field].includes(val) ? prev[field].filter(v => v !== val) : [...prev[field], val] }));
  const toggleNestedArrayItem = (section, field, val) => setForm(prev => {
    const arr = prev[section][field] || [];
    return { ...prev, [section]: { ...prev[section], [field]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] } };
  });

  // Validation
  const validateDraft = () => {
    const e = {};
    if (!form.productNameModel.trim()) e.productNameModel = 'Product Name is required';
    setErrors(e); return !Object.keys(e).length;
  };

  // Field-to-step mapping for auto-focus on validation failure
  const FIELD_STEP_MAP = {
    manufacturerId: { step: 1, selector: '[data-testid="jp-manufacturer"]' },
    productNameModel: { step: 1, selector: '[data-testid="jp-product-name"]' },
    jointType: { step: 1, selector: '[data-testid="jp-joint-type"]' },
    otherTrade: { step: 7, selector: '[data-testid="jp-other-trade"]' },
  };

  // Deterministic UI field order for focus priority
  const FIELD_UI_ORDER = ['manufacturerId', 'productNameModel', 'jointType', 'otherTrade'];

  const validateAllSteps = () => {
    const e = {};
    if (!form.manufacturerId) e.manufacturerId = 'Manufacturer is required';
    if (!form.productNameModel.trim()) e.productNameModel = 'Product Name is required';
    if (!form.jointType) e.jointType = 'Joint Type is required';
    if (form.interfaceResponsibility.responsibility === 'Other' && !form.interfaceResponsibility.otherTrade?.trim()) e.otherTrade = 'Required when Responsibility is Other';
    setErrors(e);
    if (Object.keys(e).length) {
      const firstKey = FIELD_UI_ORDER.find(k => e[k]);
      const target = firstKey && FIELD_STEP_MAP[firstKey];
      if (target) {
        setStep(target.step);
        requestAnimationFrame(() => {
          setTimeout(() => {
            const el = document.querySelector(target.selector);
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
          }, 150);
        });
      }
      return false;
    }
    return true;
  };

  // Submittal handlers
  const handlePreviewSubmittal = async (submittal) => {
    if (!draftId || submittal._status === 'pending') return;
    try {
      const res = await axios.get(`${API}/joint-protection-products/${projectId}/${draftId}/submittals/${submittal.id}/preview-url`);
      setPreviewSubmittal({ ...submittal, cloudinaryUrl: res.data?.url });
    } catch { toast.error('Failed to load preview'); }
  };

  // Save
  const handleSaveDraft = async () => {
    if (!validateDraft()) { toast.error('Please fill in required fields on Step 1 before saving'); setStep(1); return; }
    setSaving(true);
    try {
      const payload = { ...form, imageFile: undefined, imagePreview: undefined, csiItemId, status: 'draft' };
      delete payload.imageFile; delete payload.imagePreview; delete payload.submittals; delete payload.qrToken;
      let pid = draftId;
      if (!pid) {
        const res = await axios.post(`${API}/joint-protection-products/${projectId}`, payload);
        pid = res.data.id; setDraftId(pid);
        setForm(prev => ({ ...prev, qrToken: res.data.qrToken }));
        clearWizardState(companyId, 'new'); if (onRefresh) onRefresh();
      } else {
        await axios.patch(`${API}/joint-protection-products/${projectId}/${pid}`, payload);
      }
      if (form.imageFile) {
        const fd = new FormData(); fd.append('file', form.imageFile);
        const imgRes = await axios.post(`${API}/joint-protection-products/${projectId}/${pid}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const url = imgRes.data?.imageUrl || imgRes.data?.imageCloudinary?.urls?.medium;
        if (url) setForm(prev => ({ ...prev, imageFile: null, imagePreview: url }));
      }
      if (submittals.isDirty) {
        await commitSubmittals(submittals, pid, projectId, 'joint-protection-products', axios, API);
        const fresh = await axios.get(`${API}/joint-protection-products/${projectId}/${pid}`);
        submittals.init(fresh.data?.submittals || []);
      }
      saveWizardState(companyId, pid, { step, draftId: pid, form: { ...form, imageFile: null } });
      initialFormRef.current = { ...form, imageFile: null };
      toast.success('Draft saved'); if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save draft'); }
    finally { setSaving(false); }
  };

  const handleFinalSave = async () => {
    if (!validateAllSteps()) { toast.error('Please complete required fields'); return; }
    setFinalizing(true);
    try {
      const payload = { ...form, imageFile: undefined, imagePreview: undefined, csiItemId, status: 'active' };
      delete payload.imageFile; delete payload.imagePreview; delete payload.submittals; delete payload.qrToken;
      let pid = draftId;
      if (!pid) { const res = await axios.post(`${API}/joint-protection-products/${projectId}`, payload); pid = res.data.id; }
      else { await axios.patch(`${API}/joint-protection-products/${projectId}/${pid}`, payload); }
      if (form.imageFile) { const fd = new FormData(); fd.append('file', form.imageFile); await axios.post(`${API}/joint-protection-products/${projectId}/${pid}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
      if (submittals.isDirty) {
        await commitSubmittals(submittals, pid, projectId, 'joint-protection-products', axios, API);
      }
      toast.success(isEdit ? 'Product updated' : 'Product created');
      clearWizardState(companyId, pid); clearWizardState(companyId, 'new');
      if (onRefresh) onRefresh(); resetAndClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setFinalizing(false); }
  };

  const resetAndClose = () => {
    if (draftId) clearWizardState(companyId, draftId); clearWizardState(companyId, 'new');
    setStep(1); setForm(INITIAL_FORM); initialFormRef.current = INITIAL_FORM;
    setErrors({}); setDraftId(null); setMfrSearch(''); setCountrySearch('');
    setShowCloseConfirm(false); initializedRef.current = false;
    submittals.rollback();
    onOpenChange(false);
  };

  const requestClose = () => { if (isDirty) setShowCloseConfirm(true); else resetAndClose(); };
  const handleBack = () => { if (step > 1) setStep(step - 1); };
  const handleNext = () => { if (step < TOTAL_STEPS) setStep(step + 1); };

  const movementRating = parseFloat(form.movementCapability.movementRating) || 0;
  const isLowMovement = movementRating > 0 && movementRating < 25;

  const badge = (val) => {
    if (!val) return '—';
    if (val === 'Yes') return <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-600 text-white text-xs">{val}</span>;
    if (val === 'No') return <span className="inline-flex items-center px-2 py-0.5 rounded bg-red-600 text-white text-xs">{val}</span>;
    return val;
  };

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent className="max-w-4xl flex flex-col p-0 gap-0" style={{ height: '75vh', maxHeight: '85vh', minHeight: '400px' }} data-testid="jp-wizard-dialog">
        <div className="px-6 pt-5 pb-3 border-b shrink-0">
          <DialogHeader>
            <DialogTitle data-testid="jp-wizard-title">{isEdit ? 'Edit Joint Protection Product' : 'Add Joint Protection Product'}</DialogTitle>
            <DialogDescription>Step {step} of {TOTAL_STEPS}: {STEP_LABELS[step - 1]?.title || ''}</DialogDescription>
          </DialogHeader>
          <WizardStepper steps={STEP_LABELS} currentStep={step} onStepClick={setStep} testIdPrefix="jp" />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* === STEP 1: Product Identification === */}
          {step === 1 && (
            <div className="space-y-4" data-testid="jp-wizard-step-1">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Product Identification</h4>
              <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-stretch">
                {/* Image Upload Area */}
                <div className={`border-2 border-dashed rounded-lg min-h-[220px] bg-slate-50 flex flex-col items-center justify-center relative overflow-hidden`} data-testid="jp-product-image">
                  {form.imagePreview ? (
                    <>
                      <img src={form.imagePreview} alt="" className="w-full h-full object-contain p-2" />
                      <button type="button" className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 rounded-full w-6 h-6 flex items-center justify-center text-white" onClick={() => { setField('imageFile', null); setField('imagePreview', ''); }}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <ImageIcon className="h-10 w-10 text-slate-300" />
                      <span className="text-xs text-slate-400 mt-1">Product Image</span>
                      <span className="text-[10px] text-slate-300 mt-0.5">PNG, JPG, WebP (max 5MB)</span>
                    </>
                  )}
                  <div className="absolute bottom-0 inset-x-0 bg-white/90 border-t border-slate-200 flex items-center justify-center gap-1.5 py-1.5 px-2">
                    <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setShowPasteModal(true)} type="button">
                      <ClipboardPaste className="h-3.5 w-3.5 mr-1" />Paste
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => fileInputRef.current?.click()} type="button">
                      <Upload className="h-3.5 w-3.5 mr-1" />{form.imagePreview ? 'Change' : 'Upload'}
                    </Button>
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { setField('imageFile', f); setField('imagePreview', URL.createObjectURL(f)); } }} />
                </div>
                {/* Right column: Form fields */}
                <div className="space-y-3">
                  {/* Manufacturer with Add New + Clear */}
                  <FormField label="Manufacturer *" error={errors.manufacturerId}>
                    <div className="relative" ref={mfrRef}>
                      <div className="flex items-center border rounded-md px-3 py-2 cursor-pointer hover:border-orange-400 transition-colors text-sm" onClick={() => setMfrDropdownOpen(!mfrDropdownOpen)} data-testid="jp-manufacturer">
                        <span className={`flex-1 ${selectedMfr ? 'text-slate-800' : 'text-muted-foreground'}`}>{selectedMfr?.name || 'Select manufacturer...'}</span>
                        {selectedMfr && (
                          <button type="button" className="ml-1 text-slate-400 hover:text-slate-600" onClick={e => { e.stopPropagation(); setField('manufacturerId', ''); }}>
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {mfrDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-52 overflow-auto">
                          <div className="sticky top-0 bg-white p-2 border-b">
                            <div className="relative">
                              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                              <Input className="pl-8" placeholder="Search or add new..." value={mfrSearch} onChange={e => setMfrSearch(e.target.value)} autoFocus data-testid="jp-mfr-search" />
                            </div>
                          </div>
                          {filteredMfrs.map(m => (
                            <div key={m.id} className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 flex items-center ${form.manufacturerId === m.id ? 'bg-slate-50 font-medium' : ''}`}
                              onClick={() => { setField('manufacturerId', m.id); setMfrDropdownOpen(false); setMfrSearch(''); }}>
                              {form.manufacturerId === m.id ? <Check className="h-4 w-4 mr-2 text-orange-500" /> : <span className="ml-6" />}
                              <span className="flex-1">{m.name}</span>
                              {m.companyId === companyId && <UserCheck className="h-3.5 w-3.5 ml-auto text-orange-400" title="Added by you" />}
                            </div>
                          ))}
                          {filteredMfrs.length === 0 && !showAddNew && <div className="px-3 py-2 text-sm text-slate-400">No manufacturers found</div>}
                          {showAddNew && (
                            <div className="border-t px-3 py-2 text-sm cursor-pointer hover:bg-orange-50 text-orange-600 font-medium flex items-center" onClick={handleAddMfr}>
                              {creatingMfr ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                              Add new: &quot;{mfrSearch.trim()}&quot;
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </FormField>
                  <FormField label="Product Name / Model" required error={errors.productNameModel}>
                    <Input value={form.productNameModel} onChange={e => setField('productNameModel', e.target.value)} placeholder="Enter product name or model" data-testid="jp-product-name" />
                  </FormField>
                  {/* Country of Manufacturer (TI pattern) */}
                  <FormField label="Country of Manufacturer">
                    <div className="relative" ref={countryRef}>
                      <div className="flex items-center border rounded-md px-3 py-2 cursor-pointer hover:border-orange-400 transition-colors text-sm" onClick={() => setCountryDropdownOpen(true)} data-testid="jp-country">
                        {selectedCountry ? (
                          <div className="flex items-center justify-between w-full"><span className="text-sm">{selectedCountry.name}</span><X className="h-4 w-4 text-slate-400 hover:text-slate-600" onClick={(e) => { e.stopPropagation(); setField('countryCode', ''); }} /></div>
                        ) : (<span className="text-muted-foreground text-sm">Select country...</span>)}
                      </div>
                      {countryDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-52 overflow-auto">
                          <div className="sticky top-0 bg-white p-2 border-b"><div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Search country..." value={countrySearch} onChange={e => setCountrySearch(e.target.value)} autoFocus data-testid="jp-country-search" /></div></div>
                          <div className="py-1">{filteredCountries.slice(0, 50).map(c => (
                            <div key={c.code} className={`flex items-center px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm ${c.code === form.countryCode ? 'bg-slate-50 font-medium' : ''}`} onClick={() => { setField('countryCode', c.code); setCountryDropdownOpen(false); setCountrySearch(''); }}>
                              {c.code === form.countryCode && <Check className="h-4 w-4 mr-2 text-orange-500" />}<span className={c.code === form.countryCode ? '' : 'ml-6'}>{c.name}</span>
                            </div>
                          ))}</div>
                        </div>
                      )}
                    </div>
                  </FormField>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Joint Type *" error={errors.jointType}>
                  <FormSelect value={form.jointType} onValueChange={v => setField('jointType', v)} placeholder="Select joint type..." options={JOINT_TYPES} testId="jp-joint-type" />
                </FormField>
              </div>
              <FormField label="Location">
                <div className="flex flex-wrap gap-2">
                  {LOCATIONS.map(loc => (
                    <Badge key={loc} variant={form.location.includes(loc) ? 'default' : 'outline'} className={`cursor-pointer transition-colors ${form.location.includes(loc) ? 'bg-orange-500 hover:bg-orange-600' : 'hover:bg-slate-100'}`} onClick={() => toggleArrayItem('location', loc)} data-testid={`jp-loc-${loc.replace(/\s+/g, '-').toLowerCase()}`}>{loc}</Badge>
                  ))}
                </div>
              </FormField>
            </div>
          )}

          {/* === STEP 2: Movement Capability === */}
          {step === 2 && (
            <div className="space-y-4" data-testid="jp-wizard-step-2">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Movement Capability</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Movement Rating (%)" error={errors['movementCapability.movementRating']}>
                  <NumericInput value={form.movementCapability.movementRating} onChange={v => setNested('movementCapability', 'movementRating', v)} placeholder="e.g. 50" data-testid="jp-movement-rating" />
                </FormField>
                <FormField label="Movement Type">
                  <FormSelect value={form.movementCapability.movementType} onValueChange={v => setNested('movementCapability', 'movementType', v)} placeholder="Select movement type..." options={MOVEMENT_TYPES} testId="jp-movement-type" />
                </FormField>
              </div>
              {isLowMovement && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="jp-low-movement-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>Movement rating is below 25% — may be insufficient for typical joint applications</span>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Joint Width Min (in)">
                  <Input type="text" inputMode="text" value={form.movementCapability.jointWidthMin} onChange={e => setNested('movementCapability', 'jointWidthMin', sanitizeFraction(e.target.value))} placeholder="e.g. 1/4 or 0.25" data-testid="jp-joint-width-min" />
                </FormField>
                <FormField label="Joint Width Max (in)">
                  <Input type="text" inputMode="text" value={form.movementCapability.jointWidthMax} onChange={e => setNested('movementCapability', 'jointWidthMax', sanitizeFraction(e.target.value))} placeholder="e.g. 3/8 or 1.5" data-testid="jp-joint-width-max" />
                </FormField>
              </div>
            </div>
          )}

          {/* === STEP 3: Fire & Life Safety === */}
          {step === 3 && (
            <div className="space-y-4" data-testid="jp-wizard-step-3">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Fire & Life Safety</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Fire Rating (hr)">
                  <Input type="text" inputMode="decimal" value={form.fireLifeSafety.fireRating} onChange={e => setNested('fireLifeSafety', 'fireRating', sanitizeNumeric(e.target.value))} placeholder="e.g. 2" data-testid="jp-fire-rating" />
                </FormField>
                <FormField label="Fire-resistive Continuity Confirmed">
                  <FormSelect value={form.fireLifeSafety.fireResistiveContinuity} onValueChange={v => setNested('fireLifeSafety', 'fireResistiveContinuity', v)} placeholder="Select..." options={['Yes', 'No']} testId="jp-fire-continuity" />
                </FormField>
              </div>
              <FormField label="Tested Assembly (UL / ASTM E1966)">
                <Input value={form.fireLifeSafety.testedAssembly} onChange={e => setNested('fireLifeSafety', 'testedAssembly', e.target.value)} placeholder="e.g. UL System No." data-testid="jp-tested-assembly" />
              </FormField>
            </div>
          )}

          {/* === STEP 4: Environmental & Exposure === */}
          {step === 4 && (
            <div className="space-y-4" data-testid="jp-wizard-step-4">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Environmental & Exposure</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="UV Exposure Rating">
                  <FormSelect value={form.environmentalExposure.uvExposureRating} onValueChange={v => setNested('environmentalExposure', 'uvExposureRating', v)} placeholder="Select UV rating..." options={UV_RATINGS} testId="jp-uv-rating" />
                </FormField>
                <FormField label="Water Resistance">
                  <FormSelect value={form.environmentalExposure.waterResistance} onValueChange={v => setNested('environmentalExposure', 'waterResistance', v)} placeholder="Select..." options={['Yes', 'No']} testId="jp-water-resistance" />
                </FormField>
                <FormField label="Exterior / Interior Use">
                  <FormSelect value={form.environmentalExposure.exteriorInteriorUse} onValueChange={v => setNested('environmentalExposure', 'exteriorInteriorUse', v)} placeholder="Select..." options={['Exterior', 'Interior', 'Both']} testId="jp-ext-int-use" />
                </FormField>
                <FormField label="Chemical Resistance">
                  <Input value={form.environmentalExposure.chemicalResistance} onChange={e => setNested('environmentalExposure', 'chemicalResistance', e.target.value)} placeholder="Describe chemical resistance" data-testid="jp-chemical-resistance" />
                </FormField>
              </div>
            </div>
          )}

          {/* === STEP 5: Substrate Compatibility === */}
          {step === 5 && (
            <div className="space-y-4" data-testid="jp-wizard-step-5">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Substrate Compatibility</h4>
              <FormField label="Adjacent Materials">
                <div className="flex flex-wrap gap-2">
                  {ADJACENT_MATERIALS.map(m => (
                    <Badge key={m} variant={(form.substrateCompatibility.adjacentMaterials || []).includes(m) ? 'default' : 'outline'}
                      className={`cursor-pointer transition-colors ${(form.substrateCompatibility.adjacentMaterials || []).includes(m) ? 'bg-orange-500 hover:bg-orange-600' : 'hover:bg-slate-100'}`}
                      onClick={() => toggleNestedArrayItem('substrateCompatibility', 'adjacentMaterials', m)} data-testid={`jp-material-${m.toLowerCase()}`}>{m}</Badge>
                  ))}
                </div>
              </FormField>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Primer Required">
                  <FormSelect value={form.substrateCompatibility.primerRequired} onValueChange={v => setNested('substrateCompatibility', 'primerRequired', v)} placeholder="Select..." options={['Yes', 'No']} testId="jp-primer-required" />
                </FormField>
                <FormField label="Manufacturer Compatibility Confirmation">
                  <Input value={form.substrateCompatibility.manufacturerCompatConfirmation} onChange={e => setNested('substrateCompatibility', 'manufacturerCompatConfirmation', e.target.value)} placeholder="Confirmation details" data-testid="jp-compat-confirm" />
                </FormField>
              </div>
            </div>
          )}

          {/* === STEP 6: Installation Parameters === */}
          {step === 6 && (
            <div className="space-y-4" data-testid="jp-wizard-step-6">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Installation Parameters</h4>
              <FormField label="Installation Method">
                <Textarea value={form.installationParameters.installationMethod} onChange={e => setNested('installationParameters', 'installationMethod', e.target.value)} placeholder="Describe installation method" rows={3} data-testid="jp-install-method" />
              </FormField>
              <FormField label="Joint Preparation Requirement">
                <Textarea value={form.installationParameters.jointPreparation} onChange={e => setNested('installationParameters', 'jointPreparation', e.target.value)} placeholder="Describe joint preparation" rows={3} data-testid="jp-joint-prep" />
              </FormField>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Temperature Range (°F)">
                  <Input value={form.installationParameters.temperatureRange} onChange={e => setNested('installationParameters', 'temperatureRange', e.target.value)} placeholder="e.g. 40°F – 100°F" data-testid="jp-temp-range" />
                </FormField>
                <FormField label="Backer Rod Required?">
                  <FormSelect value={form.installationParameters.backerRodRequired} onValueChange={v => setNested('installationParameters', 'backerRodRequired', v)} placeholder="Select..." options={['Yes', 'No']} testId="jp-backer-rod" />
                </FormField>
              </div>
            </div>
          )}

          {/* === STEP 7: Interface Responsibility === */}
          {step === 7 && (
            <div className="space-y-4" data-testid="jp-wizard-step-7">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Interface Responsibility</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Interfaces With">
                  <FormSelect value={form.interfaceResponsibility.interfacesWith} onValueChange={v => setNested('interfaceResponsibility', 'interfacesWith', v)} placeholder="Select..." options={INTERFACES_WITH} testId="jp-interfaces-with" />
                </FormField>
                <div className="space-y-4">
                  <FormField label="Responsibility">
                    <FormSelect value={form.interfaceResponsibility.responsibility} onValueChange={v => { setNested('interfaceResponsibility', 'responsibility', v); if (v !== 'Other') setNested('interfaceResponsibility', 'otherTrade', ''); }} placeholder="Select..." options={['This subcontractor', 'Other']} testId="jp-responsibility" />
                  </FormField>
                  {form.interfaceResponsibility.responsibility === 'Other' && (
                    <FormField label="Other Trade" error={errors.otherTrade}>
                      <Input value={form.interfaceResponsibility.otherTrade} onChange={e => setNested('interfaceResponsibility', 'otherTrade', e.target.value)} placeholder="Specify trade" data-testid="jp-other-trade" />
                    </FormField>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* === STEP 8: Warranty & Maintenance === */}
          {step === 8 && (
            <div className="space-y-4" data-testid="jp-wizard-step-8">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Warranty & Maintenance</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Manufacturer Warranty (Years)">
                  <Input type="text" inputMode="decimal" value={form.warrantyMaintenance.manufacturerWarranty} onChange={e => setNested('warrantyMaintenance', 'manufacturerWarranty', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))} placeholder="e.g. 10" data-testid="jp-mfr-warranty" />
                </FormField>
                <FormField label="Installer Warranty (Years)">
                  <Input type="text" inputMode="decimal" value={form.warrantyMaintenance.installerWarranty} onChange={e => setNested('warrantyMaintenance', 'installerWarranty', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))} placeholder="e.g. 5" data-testid="jp-installer-warranty" />
                </FormField>
                <FormField label="Re-sealing Interval (Years)">
                  <Input type="text" inputMode="decimal" value={form.warrantyMaintenance.resealingInterval} onChange={e => setNested('warrantyMaintenance', 'resealingInterval', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))} placeholder="e.g. 10" data-testid="jp-reseal-interval" />
                </FormField>
                <FormField label="Maintenance Responsibility">
                  <Input value={form.warrantyMaintenance.maintenanceResponsibility} onChange={e => setNested('warrantyMaintenance', 'maintenanceResponsibility', e.target.value)} placeholder="e.g. Building owner" data-testid="jp-maintenance-resp" />
                </FormField>
              </div>
            </div>
          )}

          {/* === STEP 9: Submittals === */}
          {step === 9 && (
            <WizardSubmittalsStep
              displayList={submittals.displayList}
              onAddUpload={submittals.addUpload}
              onRemoveItem={submittals.removeItem}
              onUndoDelete={submittals.undoDelete}
              onPreview={handlePreviewSubmittal}
              docTypes={DOCUMENT_TYPES}
            />
          )}
          <SubmittalPreviewModal open={!!previewSubmittal} onClose={() => setPreviewSubmittal(null)} submittal={previewSubmittal} />

          {/* === STEP 10: Visual Coverage (Optional) === */}
          {step === 10 && (
            <div className="space-y-4" data-testid="jp-wizard-step-10">
              <h4 className="font-semibold text-sm border-b pb-2 mb-3">Visual Coverage <span className="text-xs text-slate-400 font-normal">(Optional)</span></h4>
              <p className="text-xs text-slate-500 italic">Illustrative only — not part of specification</p>

              {/* Marker Type — top */}
              <FormField label="Marker Type">
                <div className="flex flex-wrap gap-2">
                  {MARKER_TYPES.map(mt => {
                    const isExp = mt === 'Expansion joint';
                    const color = isExp ? 'bg-blue-500 hover:bg-blue-600' : 'bg-orange-500 hover:bg-orange-600';
                    const selected = form.visualJointLocation.markerType === mt;
                    const count = (form.visualJointLocation.markers || []).filter(m => m.type === mt).length;
                    return (
                      <Badge key={mt} variant={selected ? 'default' : 'outline'}
                        className={`cursor-pointer transition-colors ${selected ? color : 'hover:bg-slate-100'}`}
                        onClick={() => setNested('visualJointLocation', 'markerType', selected ? '' : mt)}
                        data-testid={`jp-marker-${mt.replace(/[\s\/]+/g, '-').toLowerCase()}`}>
                        <span className={`inline-block w-2.5 h-2.5 mr-1.5 ${isExp ? 'rounded-sm' : 'rounded-full'}`}
                          style={{ backgroundColor: selected ? '#fff' : (isExp ? '#3b82f6' : '#ea580c') }} />
                        {mt}{count > 0 ? ` (${count})` : ''}
                      </Badge>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-1">{form.visualJointLocation.markerType ? `Click on the elevation image below to place "${form.visualJointLocation.markerType}" marker` : 'Select a marker type, then click on the elevation image to place'}</p>
              </FormField>

              {/* Elevation Upload — PDF converted to image (Windows pattern) */}
              <FormField label="Elevation Upload">
                {elevationUploading && (
                  <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-2">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Processing file...</p>
                      <p className="text-xs text-blue-600">Converting elevation drawing to image</p>
                    </div>
                  </div>
                )}
                {form.visualJointLocation.elevationUrl ? (
                  <div className="border rounded-lg overflow-hidden bg-slate-50">
                    {/* Filename bar */}
                    <div className="flex items-center justify-between px-3 py-2 bg-white border-b">
                      <div className="flex items-center gap-2 text-sm min-w-0">
                        <FileText className="h-4 w-4 text-red-500 shrink-0" />
                        <span className="truncate font-medium" data-testid="jp-elevation-filename">{form.visualJointLocation.elevationFileName || 'elevation.pdf'}</span>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0 hover:bg-red-100 hover:text-red-600"
                        onClick={() => {
                          setForm(prev => ({ ...prev, visualJointLocation: { ...prev.visualJointLocation, elevationFileName: '', elevationUrl: '', elevationPublicId: '', markers: [] } }));
                        }} data-testid="jp-remove-elevation"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                    {/* Rendered elevation image with marker placement */}
                    <div className="relative overflow-auto" style={{ maxHeight: '420px' }} data-testid="jp-elevation-container">
                      {/* Zoom controls */}
                      <div className="sticky top-1 right-1 z-20 flex justify-end px-2 py-1">
                        <ZoomControls zoom={elevationZoom}
                          onZoomIn={() => setElevationZoom(z => Math.min(z + 0.25, 3))}
                          onZoomOut={() => setElevationZoom(z => Math.max(z - 0.25, 0.5))}
                          onReset={() => setElevationZoom(1)} />
                      </div>
                      <div className="relative" style={{ transform: `scale(${elevationZoom})`, transformOrigin: 'top left', width: '100%' }}>
                        <img src={form.visualJointLocation.elevationUrl} alt="Elevation" className="w-full rounded"
                          style={{ cursor: form.visualJointLocation.markerType ? 'crosshair' : 'default', display: 'block' }}
                          onClick={(e) => {
                            if (!form.visualJointLocation.markerType) return;
                            const rect = e.target.getBoundingClientRect();
                            const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
                            const y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1);
                            setForm(prev => ({ ...prev, visualJointLocation: { ...prev.visualJointLocation, markers: [...(prev.visualJointLocation.markers || []), { type: prev.visualJointLocation.markerType, x: parseFloat(x), y: parseFloat(y) }] } }));
                          }} data-testid="jp-detail-image" />
                        {(form.visualJointLocation.markers || []).map((m, i) => {
                          const isExp = m.type === 'Expansion joint';
                          return (
                            <div key={i}
                              className={`absolute border-2 border-white shadow-lg ${isExp ? 'w-3.5 h-3.5 rounded-sm' : 'w-3 h-3 rounded-full'}`}
                              style={{ left: `${m.x}%`, top: `${m.y}%`, transform: 'translate(-50%, -50%)', backgroundColor: isExp ? 'rgba(59,130,246,0.85)' : 'rgba(234,88,12,0.85)' }}
                              title={`${m.type} — click to remove`}
                              onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, visualJointLocation: { ...prev.visualJointLocation, markers: prev.visualJointLocation.markers.filter((_, j) => j !== i) } })); }}
                              data-testid={`jp-marker-${i}`} />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : !elevationUploading && (
                  <div className="border-2 border-dashed rounded-lg p-6 text-center">
                    <label className="cursor-pointer text-slate-400 hover:text-orange-500 transition-colors">
                      <Upload className="h-7 w-7 mx-auto mb-1.5" />
                      <span className="text-sm font-medium">Upload Drawing</span>
                      <p className="text-xs text-slate-400 mt-0.5">PDF, JPG, or PNG files</p>
                      <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const isPdf = f.type === 'application/pdf';
                        const isImage = ['image/jpeg', 'image/png'].includes(f.type);
                        if (!isPdf && !isImage) { toast.error('Only PDF, JPG, and PNG files are allowed'); return; }
                        setElevationUploading(true);
                        try {
                          const fd = new FormData();
                          fd.append('file', f);
                          if (isPdf) {
                            const res = await axios.post(`${API}/projects/${projectId}/elevations/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            const firstPage = res.data?.pages?.[0];
                            if (firstPage) {
                              setForm(prev => ({ ...prev, visualJointLocation: { ...prev.visualJointLocation, elevationFileName: f.name, elevationUrl: firstPage.fullImageUrl || firstPage.previewUrl, elevationPublicId: firstPage.publicId } }));
                              toast.success('Elevation uploaded');
                            }
                          } else {
                            fd.append('type', 'joint-protection');
                            const res = await axios.post(`${API}/projects/${projectId}/elevations/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            const elev = res.data?.elevation;
                            const imgUrl = elev?.imageUrl || elev?.fullImageUrl || elev?.previewUrl;
                            if (imgUrl) {
                              setForm(prev => ({ ...prev, visualJointLocation: { ...prev.visualJointLocation, elevationFileName: f.name, elevationUrl: imgUrl, elevationPublicId: elev?.publicId || '' } }));
                              toast.success('Elevation uploaded');
                            }
                          }
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload'); }
                        finally { setElevationUploading(false); e.target.value = ''; }
                      }} data-testid="jp-elevation-upload" />
                    </label>
                  </div>
                )}
              </FormField>
            </div>
          )}

          {/* === STEP 11: Review Summary === */}
          {step === 11 && (
            <div className="space-y-0" data-testid="jp-wizard-step-11">
              <div className="bg-slate-50 rounded-lg p-4 divide-y divide-slate-200">
                <ReviewProductHeader
                  imagePreview={form.imagePreview}
                  qrToken={form.qrToken}
                  infoRows={[
                    ['Product Name', form.productNameModel],
                    ['Manufacturer', selectedMfr?.name],
                    ['Country of Manufacturer', selectedCountry?.name],
                  ]}
                  testIdPrefix="jp"
                  hideInfoSection
                />
                <div className="pt-3">
                  <ReviewSection title="Product Identification" rows={[
                    ['Manufacturer', selectedMfr?.name],
                    ['Country of Manufacturer', selectedCountry?.name],
                    ['Product Name / Model', form.productNameModel],
                    ['Joint Type', form.jointType || '—'],
                    ['Location', (form.location || []).join(', ') || '—'],
                  ]} />
                </div>
                <ReviewSection title="Movement Capability" rows={[
                  ['Movement Rating (%)', isLowMovement
                    ? <span className="text-red-600 font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 inline shrink-0" />{form.movementCapability.movementRating}%</span>
                    : (form.movementCapability.movementRating ? `${form.movementCapability.movementRating}%` : '—')],
                  ['Joint Width Min (in)', form.movementCapability.jointWidthMin || '—'],
                  ['Joint Width Max (in)', form.movementCapability.jointWidthMax || '—'],
                  ['Movement Type', form.movementCapability.movementType || '—'],
                ]} />
                <ReviewSection title="Fire & Life Safety" rows={[
                  ['Fire Rating (hr)', form.fireLifeSafety.fireRating || '—'],
                  ['Tested Assembly', form.fireLifeSafety.testedAssembly || '—'],
                  ['Fire-resistive Continuity', badge(form.fireLifeSafety.fireResistiveContinuity)],
                ]} />
                <ReviewSection title="Environmental & Exposure" rows={[
                  ['UV Exposure Rating', form.environmentalExposure.uvExposureRating || '—'],
                  ['Water Resistance', badge(form.environmentalExposure.waterResistance)],
                  ['Exterior/Interior Use', form.environmentalExposure.exteriorInteriorUse || '—'],
                  ['Chemical Resistance', form.environmentalExposure.chemicalResistance || '—'],
                ]} />
                <ReviewSection title="Substrate Compatibility" rows={[
                  ['Adjacent Materials', (form.substrateCompatibility.adjacentMaterials || []).join(', ') || '—'],
                  ['Primer Required', badge(form.substrateCompatibility.primerRequired)],
                  ['Compatibility Confirmation', form.substrateCompatibility.manufacturerCompatConfirmation || '—'],
                ]} />
                <ReviewSection title="Installation Parameters" rows={[
                  ['Installation Method', form.installationParameters.installationMethod || '—'],
                  ['Joint Preparation', form.installationParameters.jointPreparation || '—'],
                  ['Temperature Range (°F)', form.installationParameters.temperatureRange || '—'],
                  ['Backer Rod Required', badge(form.installationParameters.backerRodRequired)],
                ]} />
                <ReviewSection title="Interface Responsibility" rows={[
                  ['Interfaces With', form.interfaceResponsibility.interfacesWith || '—'],
                  ['Responsibility', form.interfaceResponsibility.responsibility || '—'],
                  ...(form.interfaceResponsibility.responsibility === 'Other' ? [['Other Trade', form.interfaceResponsibility.otherTrade || '—']] : []),
                ]} />
                <ReviewSection title="Warranty & Maintenance" rows={[
                  ['Manufacturer Warranty (Years)', form.warrantyMaintenance.manufacturerWarranty || '—'],
                  ['Installer Warranty (Years)', form.warrantyMaintenance.installerWarranty || '—'],
                  ['Re-sealing Interval (Years)', form.warrantyMaintenance.resealingInterval || '—'],
                  ['Maintenance Responsibility', form.warrantyMaintenance.maintenanceResponsibility || '—'],
                ]} />
                <ReviewSection title="Visual Coverage" rows={[
                  ['Elevation PDF', form.visualJointLocation.elevationFileName || '—'],
                  ['Markers', (form.visualJointLocation.markers || []).length > 0 ? (form.visualJointLocation.markers || []).map(m => m.type).join(', ') : '—'],
                ]} />
              </div>
              <WizardSubmittalsReview displayList={submittals.displayList} docTypes={DOCUMENT_TYPES} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t shrink-0 flex items-center justify-between" data-testid="jp-wizard-footer">
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={requestClose} data-testid="jp-cancel-btn">Cancel</Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving || finalizing || !isDirty} data-testid="jp-save-draft-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save as Draft
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleBack} disabled={step === 1} data-testid="jp-back-btn"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
            {!isReviewStep && <Button onClick={handleNext} disabled={saving} data-testid="jp-next-btn">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>}
            {isReviewStep && <Button onClick={handleFinalSave} disabled={finalizing || saving} data-testid="jp-final-save-btn">{finalizing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}{isEdit ? 'Save Changes' : 'Create Product'}</Button>}
          </div>
        </div>
        <ConfirmDialog open={showCloseConfirm} title="Unsaved Changes" description="You have unsaved changes. Are you sure you want to close?" confirmLabel="Close" cancelLabel="Cancel" variant="destructive" onConfirm={resetAndClose} onClose={() => setShowCloseConfirm(false)} />
      </DialogContent>
      {showPasteModal && <ClipboardPasteModal isOpen={showPasteModal} onClose={() => setShowPasteModal(false)} onUpload={async (file) => { setPasteUploading(true); try { setField('imageFile', file); setField('imagePreview', URL.createObjectURL(file)); } finally { setPasteUploading(false); setShowPasteModal(false); } }} title="Paste Product Image" uploading={pasteUploading} />}
      {previewSubmittal && <SubmittalPreviewModal open={!!previewSubmittal} onClose={() => setPreviewSubmittal(null)} submittal={previewSubmittal} />}
    </Dialog>
  );
};

export default AddJointProtectionWizard;
